import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, addDoc, query, where, onSnapshot, orderBy, serverTimestamp, 
  doc as firestoreDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { uploadToCloudinary } from '../utils/cloudinary';
import { AY_ADLARI, GUN_ADLARI, ayKey, gunKey, ayinGunleri } from '../utils/takvim';
import { 
  LogOut, Image as ImageIcon, Calendar, Loader2, X, Send, HardHat, ChevronLeft, 
  ChevronRight, CheckCircle2, Building2, Package, Layers, FileText, AlertCircle, Clock, User
} from 'lucide-react';

export default function UstaPanel() {
  const navigate = useNavigate();
  const bugun = new Date();
  
  // Temel Stateler
  const [usta, setUsta] = useState(null);
  const [bloklar, setBloklar] = useState([]);
  const [gonderiler, setGonderiler] = useState([]);
  const [santiyeAdi, setSantiyeAdi] = useState('Yükleniyor...');
  
  // Yükleme Stateleri
  const [yukleniyor, setYukleniyor] = useState(false);
  const [resimYukleniyor, setResimYukleniyor] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // Takvim Stateleri
  const [yil, setYil] = useState(bugun.getFullYear());
  const [ay, setAy] = useState(bugun.getMonth());
  const [calismaGunleri, setCalismaGunleri] = useState({});

  // Detaylı Rapor Formu Stateleri
  const [raporForm, setRaporForm] = useState({
    blokId: '', parselId: '', kalemId: '', not: '', resimler: [], durum: 'devam'
  });
  const [onizlemeler, setOnizlemeler] = useState([]);
  const dosyaRef = useRef();

  // 1. Usta Oturumunu ve Atanan Şantiyeyi Dinle
  useEffect(() => {
    const data = localStorage.getItem('usta_oturum');
    if (!data) { navigate('/usta/giris'); return; }
    
    const ustaVerisi = JSON.parse(data);
    const unsubUsta = onSnapshot(firestoreDoc(db, 'ustalar', ustaVerisi.id), (snap) => {
      if(snap.exists()) setUsta({ id: snap.id, ...snap.data() });
    });
    return () => unsubUsta();
  }, [navigate]);

  // 2. Şantiye Adını, Blokları ve Eski Raporları Çek
  useEffect(() => {
    if (!usta?.santiyeId) return;

    // Şantiye adını al
    const unsubSantiye = onSnapshot(firestoreDoc(db, 'santiyeler', usta.santiyeId), snap => {
      if(snap.exists()) setSantiyeAdi(snap.data().ad);
    });

    // Sadece bu şantiyenin bloklarını çek
    const qBloklar = query(collection(db, 'santiyeler', usta.santiyeId, 'bloklar'), orderBy('olusturmaTarihi', 'desc'));
    const unsubBloklar = onSnapshot(qBloklar, snap => {
      setBloklar(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    // Ustanın kendi gönderdiği geçmiş raporları çek
    const qRaporlar = query(
      collection(db, 'santiyeler', usta.santiyeId, 'raporlar'), 
      where('olusturanId', '==', usta.id),
      orderBy('tarih', 'desc')
    );
    const unsubRaporlar = onSnapshot(qRaporlar, snap => {
      setGonderiler(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => { unsubSantiye(); unsubBloklar(); unsubRaporlar(); };
  }, [usta]);

  // 3. Takvim (Aylık Çalışma) Verisini Dinle
  useEffect(() => {
    if (!usta) return;
    const ak = ayKey(yil, ay);
    const unsub = onSnapshot(firestoreDoc(db, 'aylikCalisma', ak), snap => {
      if (!snap.exists()) { setCalismaGunleri({}); return; }
      const kayitlar = snap.data().kayitlar || {};
      const ustaGunleri = {};
      Object.entries(kayitlar).forEach(([gk, ustalarMap]) => {
        if (ustalarMap[usta.id]) ustaGunleri[gk] = ustalarMap[usta.id];
      });
      setCalismaGunleri(ustaGunleri);
    });
    return unsub;
  }, [usta, yil, ay]);

  // Yardımcı Fonksiyonlar
  function ayDegistir(delta) {
    let yeniAy = ay + delta, yeniYil = yil;
    if (yeniAy > 11) { yeniAy = 0; yeniYil++; }
    if (yeniAy < 0)  { yeniAy = 11; yeniYil--; }
    setAy(yeniAy); setYil(yeniYil);
  }

  const durumRenk = (durum) => {
    switch(durum) {
      case 'tamam': return { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/20', label: 'Tamamlandı' };
      case 'devam': return { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/20', label: 'Devam Ediyor' };
      case 'durak': return { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', label: 'Duraklatıldı' };
      case 'yapilmadi': return { bg: 'bg-stone-500/10', text: 'text-stone-400', border: 'border-stone-500/20', label: 'Yapılmadı' };
      default: return { bg: 'bg-stone-500/10', text: 'text-stone-400', border: 'border-stone-500/20', label: durum };
    }
  };

  // Çoklu Resim Yükleme (Cloudinary)
  const handleResimSec = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setResimYukleniyor(true);
    const yeniResimler = [];
    const yeniOnizlemeler = [];

    for (const file of files) {
      yeniOnizlemeler.push(URL.createObjectURL(file));
      try {
        const url = await uploadToCloudinary(file, setProgress);
        yeniResimler.push(url);
      } catch (err) { console.error("Yükleme hatası:", err); }
    }

    setRaporForm(prev => ({ ...prev, resimler: [...prev.resimler, ...yeniResimler] }));
    setOnizlemeler(prev => [...prev, ...yeniOnizlemeler]);
    setResimYukleniyor(false);
    setProgress(0);
  };

  // Rapor Gönderme İşlemi
  const raporGonder = async (e) => {
    e.preventDefault();
    if (!raporForm.blokId || !raporForm.kalemId) {
      alert("Lütfen Blok ve İş Kalemi seçiniz.");
      return;
    }

    setYukleniyor(true);
    try {
      const seciliBlok = bloklar.find(b => b.id === raporForm.blokId);
      const seciliKalem = seciliBlok?.kalemler?.find(k => k.id === raporForm.kalemId);
      const seciliParsel = raporForm.parselId ? seciliBlok?.parseller?.find(p => p.id === raporForm.parselId) : null;
      const now = new Date();

      await addDoc(collection(db, 'santiyeler', usta.santiyeId, 'raporlar'), {
        blokId: raporForm.blokId, 
        parselId: raporForm.parselId || null, 
        kalemId: raporForm.kalemId,
        blokAd: seciliBlok?.ad || '',
        parselAd: seciliParsel?.ad || null,
        kalemAd: seciliKalem?.ad || '',
        not: raporForm.not, 
        resimler: raporForm.resimler, 
        durum: raporForm.durum, 
        bildirimDurumu: 'bildirilmedi',
        tarih: serverTimestamp(),
        tarihStr: now.toLocaleString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
        olusturan: usta.ad,
        olusturanId: usta.id
      });

      // Formu Sıfırla
      setRaporForm({ blokId: '', parselId: '', kalemId: '', not: '', resimler: [], durum: 'devam' });
      setOnizlemeler([]);
      alert("Rapor başarıyla gönderildi!");
    } catch (err) {
      alert("Hata: " + err.message);
    } finally {
      setYukleniyor(false);
    }
  };

  // Takvim Değişkenleri
  const gunler = ayinGunleri(yil, ay);
  const ilkGunHaftaGunu = new Date(yil, ay, 1).getDay();
  const bosluklar = ilkGunHaftaGunu === 0 ? 6 : ilkGunHaftaGunu - 1;
  const ak = ayKey(yil, ay);
  const toplamCalisma = Object.keys(calismaGunleri).filter(gk => gk.startsWith(ak)).length;
  const seciliBlok = bloklar.find(b => b.id === raporForm.blokId);

  if (!usta) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-zinc-500">Yükleniyor...</div>;

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-200 pb-24 font-sans">
      
      {/* ÜST BAR */}
      <nav className="sticky top-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-zinc-900 px-5 py-4">
        <div className="max-w-xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shadow-lg shadow-emerald-500/10">
              <HardHat size={24} className="text-black" />
            </div>
            <div>
              <h1 className="text-base font-black text-white">{usta.ad}</h1>
              <div className="flex items-center gap-1.5 mt-0.5">
                <div className={`w-1.5 h-1.5 rounded-full ${usta.santiyeId ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
                <span className={`text-[10px] font-black uppercase tracking-widest ${usta.santiyeId ? 'text-emerald-500' : 'text-amber-500'}`}>
                  {usta.santiyeId ? santiyeAdi : 'GÖREV BEKLENİYOR'}
                </span>
              </div>
            </div>
          </div>
          <button onClick={() => { localStorage.removeItem('usta_oturum'); navigate('/usta/giris'); }} className="p-2.5 bg-zinc-900 rounded-xl text-zinc-500 hover:text-white transition-colors">
            <LogOut size={20} />
          </button>
        </div>
      </nav>

      <div className="max-w-xl mx-auto px-4 mt-6 space-y-8">
        
        {/* AKTİVİTE TAKVİMİ (Korundu ve Modernleştirildi) */}
        <section className="bg-zinc-900/40 border border-zinc-800 rounded-[2.5rem] p-6 shadow-xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-sm font-black text-white tracking-tight flex items-center gap-2 uppercase">
                <Calendar size={18} className="text-emerald-500" /> Mesai Takvimim
              </h2>
              <p className="text-[11px] text-zinc-500 font-bold mt-1">Bu ay toplam {toplamCalisma} gün çalışıldı.</p>
            </div>
            <div className="flex bg-black border border-zinc-800 rounded-xl p-1.5">
              <button onClick={() => ayDegistir(-1)} className="p-1 hover:text-white transition-colors"><ChevronLeft size={18} /></button>
              <div className="text-[11px] px-3 flex items-center font-black text-zinc-300 min-w-[90px] justify-center uppercase">{AY_ADLARI[ay]}</div>
              <button onClick={() => ayDegistir(1)} className="p-1 hover:text-white transition-colors"><ChevronRight size={18} /></button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1.5">
            {GUN_ADLARI.map(g => <div key={g} className="text-center text-[10px] font-black text-zinc-600 pb-2 uppercase">{g.substring(0,2)}</div>)}
            {Array.from({ length: bosluklar }).map((_, i) => <div key={`b${i}`} />)}
            {gunler.map(tarih => {
              const gk = gunKey(tarih);
              const calisti = !!calismaGunleri[gk];
              const isToday = gk === gunKey(bugun);
              return (
                <div 
                  key={gk} 
                  className={`
                    aspect-square rounded-xl relative border transition-all flex flex-col items-center justify-center
                    ${calisti ? 'bg-emerald-500 border-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.2)]' : 'bg-black/40 border-zinc-800'}
                    ${isToday ? 'ring-2 ring-white ring-offset-2 ring-offset-black' : ''}
                  `}
                >
                  <span className={`text-[10px] font-black ${calisti ? 'text-black' : 'text-zinc-600'}`}>{tarih.getDate()}</span>
                  {calisti && <CheckCircle2 size={8} className="text-black/70 absolute bottom-1.5" />}
                </div>
              );
            })}
          </div>
        </section>

        {/* DETAYLI RAPOR OLUŞTURMA (SantiyeDetay'daki gibi) */}
        <section className="bg-zinc-900 border border-zinc-800 rounded-[2.5rem] overflow-hidden shadow-2xl">
          <div className="p-5 border-b border-zinc-800 bg-zinc-900/50 flex items-center gap-2">
            <FileText size={18} className="text-emerald-500" />
            <h2 className="text-xs font-black uppercase tracking-widest text-white">Saha İş Raporu</h2>
          </div>
          
          {!usta.santiyeId ? (
            <div className="p-10 text-center space-y-3">
              <AlertCircle size={32} className="mx-auto text-amber-500" />
              <p className="text-sm font-bold text-zinc-400">Rapor oluşturmak için şantiyeye atanmalısınız.</p>
            </div>
          ) : (
            <form onSubmit={raporGonder} className="p-6 space-y-6">
              
              <div className="grid grid-cols-2 gap-3">
                {/* Blok Seçimi */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-zinc-500 uppercase ml-1">Blok Seçin</label>
                  <select 
                    className="w-full bg-black border border-zinc-800 rounded-xl p-3 text-xs font-bold outline-none focus:border-emerald-500 appearance-none"
                    value={raporForm.blokId}
                    onChange={(e) => setRaporForm({...raporForm, blokId: e.target.value, parselId: '', kalemId: ''})}
                    required
                  >
                    <option value="">Blok seçin...</option>
                    {bloklar.map(b => <option key={b.id} value={b.id}>{b.ad}</option>)}
                  </select>
                </div>

                {/* İş Kalemi Seçimi */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-zinc-500 uppercase ml-1">İş Kalemi</label>
                  <select 
                    className="w-full bg-black border border-zinc-800 rounded-xl p-3 text-xs font-bold outline-none focus:border-emerald-500 appearance-none disabled:opacity-50"
                    value={raporForm.kalemId}
                    disabled={!raporForm.blokId}
                    onChange={(e) => setRaporForm({...raporForm, kalemId: e.target.value})}
                    required
                  >
                    <option value="">İş seçin...</option>
                    {seciliBlok?.kalemler?.map(k => <option key={k.id} value={k.id}>{k.ad}</option>)}
                  </select>
                </div>
              </div>

              {/* Parsel Seçimi */}
              {seciliBlok?.parseller?.length > 0 && (
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-zinc-500 uppercase ml-1">Parsel (Opsiyonel)</label>
                  <select 
                    className="w-full bg-black border border-zinc-800 rounded-xl p-3 text-xs font-bold outline-none focus:border-emerald-500 appearance-none"
                    value={raporForm.parselId}
                    onChange={(e) => setRaporForm({...raporForm, parselId: e.target.value})}
                  >
                    <option value="">Parsel seçilmedi (Tüm blok)</option>
                    {seciliBlok.parseller.map(p => <option key={p.id} value={p.id}>{p.ad}</option>)}
                  </select>
                </div>
              )}

              {/* İş Durumu Butonları */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-zinc-500 uppercase ml-1">İş Durumu</label>
                <div className="grid grid-cols-4 gap-2">
                  {[{ id: 'devam', label: 'Devam', color: 'yellow' }, { id: 'tamam', label: 'Tamam', color: 'green' }, { id: 'durak', label: 'Durak', color: 'red' }, { id: 'yapilmadi', label: 'Yapılmadı', color: 'stone' }].map(d => (
                    <button 
                      key={d.id} type="button" 
                      onClick={() => setRaporForm({...raporForm, durum: d.id})} 
                      className={`py-3 rounded-xl text-[9px] font-black uppercase tracking-wider transition-all 
                        ${raporForm.durum === d.id ? `bg-${d.color}-500/20 text-${d.color}-400 border border-${d.color}-500/30` : 'bg-black text-zinc-600 border border-zinc-800 hover:border-zinc-600'}`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Açıklama */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-zinc-500 uppercase ml-1">İş Notu / Açıklama</label>
                <textarea 
                  rows={3}
                  className="w-full bg-black border border-zinc-800 rounded-xl p-4 text-sm outline-none focus:border-emerald-500 resize-none placeholder-zinc-700 font-medium"
                  placeholder="Sahadaki ilerleme, karşılaşılan sorunlar..."
                  value={raporForm.not}
                  onChange={(e) => setRaporForm({...raporForm, not: e.target.value})}
                />
              </div>

              {/* Görseller */}
              <div className="space-y-3">
                <label className="text-[10px] font-black text-zinc-500 uppercase ml-1">Saha Görselleri</label>
                <div className="flex flex-wrap gap-2">
                  {onizlemeler.map((url, i) => (
                    <div key={i} className="relative w-16 h-16 rounded-xl overflow-hidden border border-zinc-800">
                      <img src={url} className="w-full h-full object-cover" />
                    </div>
                  ))}
                  <button 
                    type="button" 
                    onClick={() => dosyaRef.current.click()}
                    className="w-16 h-16 rounded-xl border-2 border-dashed border-zinc-800 bg-black flex flex-col items-center justify-center text-zinc-600 hover:text-emerald-500 hover:border-emerald-500 transition-all"
                  >
                    <ImageIcon size={18} />
                  </button>
                </div>
                <input ref={dosyaRef} type="file" multiple accept="image/*" className="hidden" onChange={handleResimSec} disabled={resimYukleniyor} />
              </div>

              {/* Gönder Butonu */}
              <button 
                type="submit" 
                disabled={yukleniyor || resimYukleniyor}
                className="w-full bg-white text-black py-4 rounded-2xl text-xs font-black flex items-center justify-center gap-2 hover:bg-emerald-500 transition-all disabled:bg-zinc-800 disabled:text-zinc-500 shadow-xl shadow-white/5 uppercase tracking-widest"
              >
                {yukleniyor ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                {resimYukleniyor ? `RESİMLER YÜKLENİYOR %${progress}` : 'RAPORU SİSTEME GÖNDER'}
              </button>
            </form>
          )}
        </section>

        {/* GEÇMİŞ RAPORLAR */}
        <div className="space-y-4">
          <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-600 px-2 flex items-center gap-2">
            <Layers size={14}/> Gönderdiğim Raporlar
          </h3>
          {gonderiler.length === 0 ? (
            <div className="text-center py-10 text-zinc-600 text-xs italic">Henüz bu şantiye için rapor oluşturmadınız.</div>
          ) : (
            gonderiler.map(g => {
              const durum = durumRenk(g.durum);
              return (
                <div key={g.id} className="bg-zinc-900/40 border border-zinc-800/50 rounded-[2rem] p-5 space-y-4 hover:border-zinc-700 transition-all">
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-white font-black text-sm truncate">
                        {g.kalemAd} {g.parselAd && <span className="text-zinc-500 font-medium text-xs"> — {g.parselAd}</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 text-[9px] text-zinc-500 font-bold uppercase tracking-tighter">
                        <span className="flex items-center gap-1"><Building2 size={10} /> {g.blokAd}</span>
                        <span className="flex items-center gap-1"><Clock size={10} /> {g.tarihStr?.split(' ')[1]}</span>
                      </div>
                    </div>
                    <span className={`text-[9px] font-black uppercase px-2 py-1.5 rounded-lg ${durum.bg} ${durum.text} border ${durum.border} flex-shrink-0`}>
                      {durum.label}
                    </span>
                  </div>

                  {g.not && <p className="text-xs text-zinc-400 leading-relaxed font-medium bg-black/40 p-3 rounded-xl border border-zinc-800/50">"{g.not}"</p>}
                  
                  {g.resimler?.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                      {g.resimler.map((img, i) => (
                        <img key={i} src={img} className="w-16 h-16 rounded-xl object-cover flex-shrink-0 border border-zinc-800 cursor-pointer hover:opacity-80" onClick={() => window.open(img, '_blank')} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

      </div>
    </div>
  );
}